import { useEffect, useMemo, useState } from 'react';
import { Alert, Box, Chip, FormControl, LinearProgress, MenuItem, Pagination, Paper, Select, SelectChangeEvent, Snackbar, SnackbarCloseReason, Table, TableBody, TableCell, TableContainer, TableFooter, TableHead, TableRow, Typography, useMediaQuery, useTheme, alpha } from '@mui/material';
import { useSearchParams } from 'react-router-dom';
import {
  LinhasEsqueletoTabela,
  ListagemBuscaField,
  paperListagemSx,
  tableContainerListagemSx,
} from '../../../shared/components';
import { LayoutBaseDePagina } from '../../../shared/layouts';
import { useDebounce, useListagemAltura, useListagemKeyboardNav } from '../../../shared/hooks';
import { Environment } from '../../../shared/environment';
import { IListagemSessaoCaixa, SessaoCaixaService, TStatusSessaoCaixa } from '../../../shared/services/api/Financeiro/SessaoCaixa';
import { brand } from '../../../shared/themes';
import PointOfSaleRoundedIcon from '@mui/icons-material/PointOfSaleRounded';
import { CelulaListaMobile, LinhaListagemMobile, ListaMobile, useCelular } from '../../../shared/components/listagem-mobile';

const OPCOES_POR_PAGINA = [10, 25, 50, 100];

const LABEL_STATUS_SESSAO: Record<TStatusSessaoCaixa, string> = {
  aberta: 'Aberta',
  fechada: 'Fechada',
  cancelada: 'Cancelada',
};

const COR_STATUS_SESSAO: Record<TStatusSessaoCaixa, 'success' | 'default' | 'warning'> = {
  aberta: 'success',
  fechada: 'default',
  cancelada: 'warning',
};

const formatarData = (valor?: string) => {
  if (!valor) return '—';
  return new Date(valor).toLocaleString('pt-BR', { dateStyle: 'short', timeStyle: 'short' });
};

export const ListagemDeSessoesAbertas: React.FC = () => {
  const listaCelular = useCelular();
  const [searchParams, setSearchParams] = useSearchParams();
  const { debounce } = useDebounce();
  const theme = useTheme();
  // No celular esconde Número/Abertura/Fechamento (secundárias) em vez de deixar a
  // tabela rolar na horizontal — cabe tudo na tela só com Caixa/Usuário/Status.
  const smDown = useMediaQuery(theme.breakpoints.down('sm'));

  const [rows, setRows] = useState<IListagemSessaoCaixa[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [totalCount, setTotalCount] = useState(0);
  const [openmsg, setOpenmsg] = useState(false);
  const [msg, setMsg] = useState('');
  const [tms, setTms] = useState<'warning' | 'error' | 'info' | 'success'>('warning');
  const ROWS_CACHE_KEY = 'sessoescaixa';

  const { tabelaRef, containerHeight, rowsPerPage, setRowsPerPage } = useListagemAltura({
    cacheKey: ROWS_CACHE_KEY,
    valorInicial: Environment.LIMITE_DE_LINHAS,
    rowHeight: 48,
  });

  const handleChangeRowsPerPage = (event: SelectChangeEvent<number>) => {
    setRowsPerPage(Number(event.target.value));
  };

  const handleCloseMsg = (
    event?: React.SyntheticEvent | Event,
    reason?: SnackbarCloseReason,
  ) => {
    if (reason === 'clickaway') {
      return;
    }
    setOpenmsg(false);
  };

  const filter = useMemo(() => {
    return searchParams.get('filter') || '';
  }, [searchParams]);

  const pagina = useMemo(() => {
    return Number(searchParams.get('pagina') || '1');
  }, [searchParams]);

  useEffect(() => {
    setIsLoading(true);

    debounce(() => {
      SessaoCaixaService.getAll(pagina, filter, rowsPerPage)
        .then((result) => {
          setIsLoading(false);
          if (result instanceof Error) {
            setTms('error');
            setMsg(result.message);
            setOpenmsg(true);
          } else {
            setTotalCount(result.totalCount);
            setRows(result.data);
          }
        });
    });
  }, [pagina, filter, rowsPerPage]);

  useEffect(() => {
    if (pagina !== 1) {
      setSearchParams({ filter, pagina: '1' }, { replace: true });
    }
  }, [rowsPerPage]);

  const primeiroItem = totalCount === 0 ? 0 : (pagina - 1) * rowsPerPage + 1;
  const ultimoItem = Math.min(pagina * rowsPerPage, totalCount);
  const totalPaginas = Math.max(1, Math.ceil(totalCount / rowsPerPage));

  // Sem F8 aqui (omitindo tiposBusca/onTrocarTipo): a tela tem só um campo de busca livre (por
  // número da sessão), sem seletor de tipo — não há o que ciclar. Sem onEnter também: é uma
  // listagem só de consulta, sem tela de detalhe pra navegar.
  const { pesquisaRef, linhaRefCallback, onBuscaKeyDown, onLinhaKeyDown } = useListagemKeyboardNav({
    rowsLength: rows.length,
    pagina,
    totalPaginas,
    onIrParaPagina: (novaPagina) => setSearchParams({ filter, pagina: novaPagina.toString() }, { replace: true }),
    rowsDep: [rows],
  });

  return (
    <LayoutBaseDePagina
      titulo='Sessões Abertas'
      subtitulo='Consulte as sessões de caixa por número, caixa, data de abertura e de fechamento.'
      barraDeFerramentas={
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5, mx: 1, mb: 2.5 }}>
          <ListagemBuscaField
            value={filter}
            onChange={(valor) => setSearchParams({ filter: valor, pagina: '1' }, { replace: true })}
            placeholder='Pesquisar por número da sessão...'
            inputRef={pesquisaRef}
            onKeyDown={onBuscaKeyDown}
          />
        </Box>
      }
    >
      <Snackbar anchorOrigin={{ vertical: 'top', horizontal: 'center' }} open={openmsg} autoHideDuration={tms === 'error' ? null : 900} onClose={handleCloseMsg}>
        <Alert
          onClose={handleCloseMsg}
          severity={tms}
          variant="filled"
          sx={{ width: '100%' }}
        >
          {msg}
        </Alert>
      </Snackbar>

      <Paper elevation={0} sx={paperListagemSx(theme)}>
        <TableContainer
          ref={tabelaRef}
          sx={tableContainerListagemSx(containerHeight, isLoading || rows.length >= rowsPerPage)}
        >
          {/* No desktop, minWidth (não width:'100%') evita que tableLayout:'fixed'
              espreme as colunas abaixo do legível — quem rola é o TableContainer. No
              celular (smDown) Número/Abertura/Fechamento somem (ver abaixo), então
              width:'100%' sozinho cabe as 3 colunas restantes na tela. */}
          <Table size='small' sx={{ width: '100%', minWidth: smDown ? 0 : 750, tableLayout: 'fixed' }}>
            {!listaCelular && (
            <TableHead>
              <TableRow>
                {!smDown && <TableCell sx={{ width: '15%' }}>Número</TableCell>}
                <TableCell sx={{ width: smDown ? undefined : '20%' }}>Caixa</TableCell>
                <TableCell sx={{ width: smDown ? undefined : '20%' }}>Usuário</TableCell>
                {!smDown && <TableCell sx={{ width: '17%' }}>Abertura</TableCell>}
                {!smDown && <TableCell sx={{ width: '17%' }}>Fechamento</TableCell>}
                <TableCell align='center' sx={{ width: smDown ? '104px' : '11%' }}>Status</TableCell>
              </TableRow>
            </TableHead>
            )}

            {isLoading && rows.length === 0 && (
              <TableBody>
                <LinhasEsqueletoTabela
                  colunas={smDown ? [
                    { width: '70%' },
                    { width: '70%' },
                    { variant: 'rounded', width: '60%', height: 22, align: 'center' },
                  ] : [
                    { width: '60%' },
                    { width: '70%' },
                    { width: '70%' },
                    { width: '65%' },
                    { width: '65%' },
                    { variant: 'rounded', width: '60%', height: 22, align: 'center' },
                  ]}
                />
              </TableBody>
            )}
            <TableBody>
              {listaCelular ? (
              <CelulaListaMobile>
                <ListaMobile
                  linhas={rows}
                  chave={(row) => row.idsessao}
                  renderizar={(row) => (
                    <LinhaListagemMobile
                      avatar={row.nomeUsuario || '?'}
                      titulo={row.nomeConta}
                      detalhes={[row.numero ? `Nº ${row.numero}` : null, row.nomeUsuario, `Abertura ${formatarData(row.dataabertura)}`, row.datafechamento ? `Fech. ${formatarData(row.datafechamento)}` : null]}
                      etiqueta={{ rotulo: LABEL_STATUS_SESSAO[row.status], cor: COR_STATUS_SESSAO[row.status] }}
                    />
                  )}
                />
              </CelulaListaMobile>
            ) : rows.map((row, index) => (
                <TableRow
                  key={row.idsessao}
                  ref={linhaRefCallback(index)}
                  tabIndex={0}
                  onKeyDown={onLinhaKeyDown(index)}
                  sx={{
                    outline: 'none',
                    '&:last-child td': { borderBottom: 0 },
                    '& td': {
                      py: smDown ? 0.75 : 1.25,
                      px: smDown ? 0.75 : 1.5,
                      fontSize: smDown ? '0.75rem' : '0.8125rem',
                      borderBottom: `1px solid ${theme.torq.border.default}`,
                    },
                    '&:focus-visible': {
                      outline: `2px solid ${theme.torq.border.focus}`,
                      outlineOffset: '-2px',
                      backgroundColor: alpha(theme.palette.primary.main, 0.05),
                    },
                  }}
                >
                  {!smDown && (
                    <TableCell>
                      <Typography fontFamily="monospace" fontWeight={600} noWrap sx={{ fontSize: '0.8125rem', color: theme.torq.text.primary }}>
                        {row.numero || '—'}
                      </Typography>
                    </TableCell>
                  )}
                  <TableCell>
                    <Typography noWrap sx={{ fontSize: '0.8125rem', color: theme.torq.text.primary }}>
                      {row.nomeConta}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography noWrap sx={{ fontSize: '0.8125rem', color: theme.torq.text.primary }}>
                      {row.nomeUsuario}
                    </Typography>
                  </TableCell>
                  {!smDown && (
                    <TableCell>
                      <Typography noWrap sx={{ fontSize: '0.8125rem', color: theme.torq.text.secondary }}>
                        {formatarData(row.dataabertura)}
                      </Typography>
                    </TableCell>
                  )}
                  {!smDown && (
                    <TableCell>
                      <Typography noWrap sx={{ fontSize: '0.8125rem', color: theme.torq.text.secondary }}>
                        {formatarData(row.datafechamento)}
                      </Typography>
                    </TableCell>
                  )}
                  <TableCell align='center'>
                    <Chip
                      label={LABEL_STATUS_SESSAO[row.status]}
                      color={COR_STATUS_SESSAO[row.status]}
                      size='small'
                      sx={{ fontSize: '0.6875rem', fontWeight: 600 }}
                    />
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>

            {rows.length === 0 && !isLoading && (
              <TableBody>
                <TableRow>
                  <TableCell colSpan={smDown ? 3 : 6} align='center' sx={{ py: 3 }}>
                    <Box>
                      <PointOfSaleRoundedIcon sx={{
                        fontSize: 35,
                        color: theme.palette.mode === 'dark'
                          ? alpha(theme.palette.text.secondary, 0.4)
                          : 'text.secondary',
                        mb: 1
                      }} />
                      <Typography variant='h6' color='text.secondary' gutterBottom fontSize="0.85rem">
                        {Environment.LISTAGEM_VAZIA}
                      </Typography>
                      <Typography variant='body2' color='text.secondary' fontSize="0.7rem">
                        {filter ? 'Tente ajustar os filtros de busca' : 'Nenhuma sessão de caixa foi aberta ainda'}
                      </Typography>
                    </Box>
                  </TableCell>
                </TableRow>
              </TableBody>
            )}

            <TableFooter>
              {isLoading && (
                <TableRow>
                  <TableCell colSpan={smDown ? 3 : 6} sx={{ p: 0 }}>
                    <LinearProgress sx={{ height: '1.5px' }} />
                  </TableCell>
                </TableRow>
              )}
              <TableRow>
                <TableCell colSpan={smDown ? 3 : 6} sx={{ py: 1.25, px: 2, borderTop: `1px solid ${theme.torq.border.default}` }}>
                  <Box display='flex' alignItems='center' justifyContent='space-between' flexWrap='wrap' gap={1.5}>
                    <Typography variant='caption' color='text.secondary'>
                      {totalCount === 0
                        ? 'Nenhuma sessão encontrada'
                        : `Mostrando de ${primeiroItem} a ${ultimoItem} de ${totalCount} ${totalCount !== 1 ? 'sessões' : 'sessão'}`}
                    </Typography>

                    {totalCount > rowsPerPage && (
                      <Pagination
                        page={pagina}
                        count={totalPaginas}
                        onChange={(_, newPage) => setSearchParams({ filter, pagina: newPage.toString() }, { replace: true })}
                        size="small"
                        shape="rounded"
                        showFirstButton
                        showLastButton
                        siblingCount={1}
                        boundaryCount={1}
                        sx={{
                          '& .MuiPaginationItem-root.Mui-selected': {
                            backgroundColor: brand.menuBase,
                            color: '#fff',
                            '&:hover': { backgroundColor: '#012D3D' },
                          },
                        }}
                      />
                    )}

                    <FormControl size='small' sx={{ minWidth: 110 }}>
                      <Select<number> value={rowsPerPage} onChange={handleChangeRowsPerPage}>
                        {(OPCOES_POR_PAGINA.includes(rowsPerPage) ? OPCOES_POR_PAGINA : [...OPCOES_POR_PAGINA, rowsPerPage].sort((a, b) => a - b)).map((opcao) => (
                          <MenuItem key={opcao} value={opcao}>{opcao} / página</MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Box>
                </TableCell>
              </TableRow>
            </TableFooter>
          </Table>
        </TableContainer>
      </Paper>
    </LayoutBaseDePagina>
  );
};
